import { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import { downloadImageLocallyFromS3, extractKeyFromPresignedUrl } from "../../config/aws";
import { sendToBackend } from "../../utils/utilities";
import { fal } from "@fal-ai/client";
import { checkAvailableCredits, decreaseCredits } from "../../dbApiFunctions";
import { showErrorAlert } from "../../utils/sweetAlertConfig";

// Configure fal.ai client with credentials
fal.config({
    credentials: process.env.REACT_APP_FAL_AI_KEY
});


const DownloadImageModel = ({ onClose, imageData, creditsAvailable, setCreditsAvailable }) => {
    const [selectedFormat, setSelectedFormat] = useState("WEBP");
    const [selectedSize, setSelectedSize] = useState("preview");
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadStatus, setDownloadStatus] = useState("");
    const [upscaledUrl, setUpscaledUrl] = useState("");

    // Debug info on mount
    useEffect(() => {
        // console.log("DownloadImageModel mounted with image data:", imageData);
    }, [imageData]);

    const handleDownload = async () => {
        // console.log("handleDownload called with imageData:", imageData, "selectedFormat:", selectedFormat, "selectedSize:", selectedSize);
        if (!imageData || !imageData.src) {
            console.error("No image data available for download");
            setDownloadStatus("Error: No image data available");
            return;
        }

        setIsDownloading(true);
        try {
            if (selectedSize === "preview") {
                // console.log("Selected size is preview. Extracting S3 path...");
                const s3_path = extractKeyFromPresignedUrl(imageData.src);
                // console.log("Extracted s3_path:", s3_path);
                const new_s3_path = s3_path.replace("lowQuality", "highQuality");
                // console.log("Converted to highQuality path:", new_s3_path, "Format:", selectedFormat);

                await downloadImageLocallyFromS3(new_s3_path, selectedFormat);
                // console.log("Preview image download initiated.");
            } else {
                // console.log("Selected size is upscaled (hd). Checking credits...");
                const email = sessionStorage.getItem('email');
                const password = sessionStorage.getItem('password');
                // console.log("Email:", email, "Password:", !!password);
                const response = await checkAvailableCredits(email, password, 1);
                // console.log("Credit check response:", response);
                if (!response.status) {
                    showErrorAlert(response.message, 'Insufficient Credits');
                    return;
                }
                setDownloadStatus("Preparing image for upscaling...");
                // console.log("Preparing image for upscaling...");

                setDownloadStatus("Upscaling image...");
                // console.log("Calling fal.subscribe for upscaling...");
                // Call Fal.ai upscaler using the correct model name                
                const result = await fal.subscribe('fal-ai/ccsr', {
                    input: {
                        image_url: imageData.src,   // ← note the field name
                        scale: 2,
                    }
                });
                // console.log("Fal upscaler result:", result.data);
                const upscaledUrl = result.data.image.url;
                // console.log("Upscaled image URL:", upscaledUrl);

                if (!upscaledUrl) {
                    console.error("No image URL in response from upscaler");
                    throw new Error("No image URL in response from upscaler");
                }

                setDownloadStatus("Downloading upscaled image...");
                // console.log("Fetching upscaled image from:", upscaledUrl);
                const upscaledResponse = await fetch(upscaledUrl);

                if (!upscaledResponse.ok) {
                    console.error("Failed to fetch upscaled image. Response:", upscaledResponse);
                    throw new Error('Failed to fetch upscaled image');
                }
                const upscaledBlob = await upscaledResponse.blob();
                // console.log("Fetched upscaled image blob:", upscaledBlob);

                const url = URL.createObjectURL(upscaledBlob);
                // console.log("Created object URL for download:", url);

                const a = document.createElement('a');
                a.href = url;
                a.download = `upscaled-image-${Date.now()}.${selectedFormat.toLowerCase()}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                // console.log("Upscaled image download triggered and object URL revoked.");

                await decreaseCredits(email, password, 1, 'upscale');
                setCreditsAvailable(creditsAvailable - 1);
                setDownloadStatus("Upscaled image downloaded successfully!");
                // console.log("Credits decreased and download status updated.");
            }
        } catch (error) {
            console.error("Error in download process:", error);
            setDownloadStatus(`Error: ${error.message}`);
        } finally {
            setIsDownloading(false);
            // console.log("handleDownload finished. isDownloading set to false.");
        }
    }

    return (
        <div className="bg-white p-3 rounded-xl shadow-lg w-64 relative transform transition-all duration-200">
            <button
                onClick={onClose}
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
                <FaTimes />
            </button>
            <div className="mb-3">
                <h2 className="text-gray-600 text-xs font-semibold mb-1 uppercase tracking-wider">Size</h2>
                <div className="space-y-1">
                    <div
                        onClick={() => setSelectedSize("preview")}
                        className={`border-2 rounded-lg p-2 cursor-pointer transition-colors duration-200 ${selectedSize === "preview"
                            ? "border-purple-500 bg-purple-50"
                            : "border-gray-200 hover:border-purple-300"
                            }`}
                    >
                        <p className={`font-semibold text-sm ${selectedSize === "preview" ? "text-purple-600" : "text-gray-800"
                            }`}>Preview</p>
                        <p className="text-gray-500 text-xs">1024 x 1024</p>
                    </div>
                    <div
                        onClick={() => setSelectedSize("hd")}
                        className={`border-2 rounded-lg p-2 cursor-pointer transition-colors duration-200 ${selectedSize === "hd"
                            ? "border-purple-500 bg-purple-50"
                            : "border-gray-200 hover:border-purple-300"
                            }`}
                    >
                        <p className={`font-semibold text-sm ${selectedSize === "hd" ? "text-purple-600" : "text-gray-800"
                            }`}>Upscaled</p>
                        <p className="text-gray-500 text-xs">2048 x 2048</p>
                    </div>
                </div>
            </div>
            <div className="mb-3">
                <h2 className="text-gray-600 text-xs font-semibold mb-1 uppercase tracking-wider">Format</h2>
                <div className="flex space-x-2">
                    {["WEBP", "JPG", "PNG"].map((format) => (
                        <button
                            key={format}
                            onClick={() => setSelectedFormat(format)}
                            className={`flex-1 border-2 text-xs font-semibold rounded-lg px-2 py-1.5 transition-all duration-200 ${selectedFormat === format
                                ? "border-purple-500 text-purple-600 bg-purple-50"
                                : "border-gray-200 text-gray-600 hover:border-purple-300 hover:text-purple-500"
                                }`}
                        >
                            {format}
                        </button>
                    ))}
                </div>
            </div>

            {downloadStatus && (
                <div className="mb-2 text-xs text-center">
                    <span className={downloadStatus.includes("Error") ? "text-red-500" : "text-green-600"}>
                        {downloadStatus}
                    </span>
                </div>
            )}

            <button
                onClick={handleDownload}
                disabled={isDownloading}
                className={`w-full ${isDownloading ? 'bg-purple-400' : 'bg-purple-600 hover:bg-purple-700'} text-white text-sm font-semibold rounded-lg py-1.5 px-4 transition-colors duration-200 shadow-sm`}
            >
                {isDownloading ? (
                    <div className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Downloading...
                    </div>
                ) : "Download"}
            </button>
        </div>
    );
};

export default DownloadImageModel;